import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, ROLES } from '../contexts/AuthContext';
import { toast } from 'sonner';
import {
  NativeButton,
  NativeCard,
  NativeCardHeader,
  NativeCardTitle,
  NativeCardContent,
  NativeInput,
  NativeSelect,
  NativeBadge,
  NativeModal,
  NativeTable,
  NativeTableHeader,
  NativeTableHead,
  NativeTableBody,
  NativeTableRow,
  NativeTableCell,
  EmptyState
} from '../components/NativeComponents';
import { 
  Loader2, 
  Plus, 
  Pencil, 
  Trash2,
  RefreshCw,
  DollarSign,
  Clock,
  Calendar
} from 'lucide-react';

// API URL - Production'da boşsa relative URL kullan
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// API Service
const tariffsService = {
  getAll: async (token) => {
    if (!token) {
      throw new Error('Oturum bulunamadı, lütfen tekrar giriş yapın');
    }
    
    const response = await fetch(`${API_URL}/api/tariffs`, {
      headers: { 
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json'
      }
    });
    
    const contentType = response.headers.get('content-type');
    
    if (!response.ok) {
      if (contentType && contentType.includes('application/json')) {
        const err = await response.json();
        throw new Error(err.detail || 'Tarifeler yüklenemedi');
      }
      throw new Error(`Sunucu hatası: ${response.status}`);
    }
    
    if (!contentType || !contentType.includes('application/json')) {
      console.error('Unexpected content-type:', contentType);
      throw new Error('Sunucu geçersiz yanıt döndürdü');
    }
    
    return response.json();
  },
  create: async (token, data) => {
    if (!token) throw new Error('Oturum bulunamadı');
    
    const response = await fetch(`${API_URL}/api/tariffs`, {
      method: 'POST',
      headers: { 
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(data)
    });
    
    // Response'u bir kere clone edip oku
    const responseClone = response.clone();
    
    if (!response.ok) {
      try {
        const err = await responseClone.json();
        throw new Error(err.detail || 'Tarife oluşturulamadı');
      } catch (parseError) {
        throw new Error(`Tarife oluşturulamadı: ${response.status}`);
      }
    }
    
    return response.json();
  },
  update: async (token, id, data) => {
    if (!token) throw new Error('Oturum bulunamadı');
    
    const response = await fetch(`${API_URL}/api/tariffs/${id}`, {
      method: 'PUT',
      headers: { 
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(data)
    });
    
    if (!response.ok) {
      try {
        const responseClone = response.clone();
        const err = await responseClone.json();
        throw new Error(err.detail || 'Tarife güncellenemedi');
      } catch (parseError) {
        if (parseError.message.includes('Tarife')) throw parseError;
        throw new Error(`Tarife güncellenemedi: ${response.status}`);
      }
    }
    return response.json();
  },
  delete: async (token, id) => {
    if (!token) throw new Error('Oturum bulunamadı');
    
    const response = await fetch(`${API_URL}/api/tariffs/${id}`, {
      method: 'DELETE',
      headers: { 
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json'
      }
    });
    if (!response.ok) {
      try {
        const responseClone = response.clone();
        const err = await responseClone.json();
        throw new Error(err.detail || 'Tarife silinemedi');
      } catch (parseError) {
        if (parseError.message.includes('Tarife')) throw parseError;
        throw new Error(`Tarife silinemedi: ${response.status}`);
      }
    }
  }
};

const sitesService = {
  getAll: async (token) => {
    if (!token) {
      throw new Error('Oturum bulunamadı');
    }
    
    const response = await fetch(`${API_URL}/api/sites`, {
      headers: { 
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json'
      }
    });
    
    const contentType = response.headers.get('content-type');
    
    if (!response.ok) {
      if (contentType && contentType.includes('application/json')) {
        const err = await response.json();
        throw new Error(err.detail || 'Siteler yüklenemedi');
      }
      throw new Error(`Sunucu hatası: ${response.status}`);
    }
    
    if (!contentType || !contentType.includes('application/json')) {
      console.error('Unexpected content-type:', contentType);
      throw new Error('Sunucu geçersiz yanıt döndürdü');
    }
    
    return response.json();
  }
};

const DAY_TYPE_LABELS = {
  everyday: 'Her Gün',
  weekday: 'Hafta İçi',
  weekend: 'Hafta Sonu'
};

const CURRENCY_OPTIONS = [
  { value: 'TRY', label: 'TL (₺)' },
  { value: 'USD', label: 'USD ($)' },
  { value: 'EUR', label: 'EUR (€)' }
];

const DAY_TYPE_OPTIONS = [
  { value: 'everyday', label: 'Her Gün' },
  { value: 'weekday', label: 'Hafta İçi (Pzt-Cum)' },
  { value: 'weekend', label: 'Hafta Sonu (Cmt-Paz)' }
];

export default function TariffsPage() {
  const { user, isSuperAdmin, isHesapYoneticisi, getEffectiveRole, getEffectiveSiteId } = useAuth();
  const [tariffs, setTariffs] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Simülasyon desteği
  const effectiveRole = getEffectiveRole ? getEffectiveRole() : user?.role;
  const effectiveSiteId = getEffectiveSiteId ? getEffectiveSiteId() : user?.site_id;
  const isEffectiveHesapYoneticisi = effectiveRole === 'hesap_yoneticisi' || effectiveRole === 'site_manager';
  const isEffectiveBireyselHesap = effectiveRole === 'bireysel_hesap' || effectiveRole === 'bireysel_yonetici';
  
  // Bireysel Hesap için yeni tarife oluşturmayı engelle
  const canCreateTariff = !isEffectiveBireyselHesap;
  
  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [editingTariffId, setEditingTariffId] = useState(null);
  
  // Delete modal
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingTariff, setDeletingTariff] = useState(null);
  
  // Form state
  const [form, setForm] = useState({
    name: '',
    site_id: '',
    price_per_kwh: '',
    currency: 'TRY',
    start_time: '00:00',
    end_time: '23:59',
    day_type: 'everyday',
    is_active: true
  });
  
  const token = localStorage.getItem('hims_token');
  const isSiteManager = user?.role === ROLES.SITE_MANAGER;
  
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      let [tariffsData, sitesData] = await Promise.all([
        tariffsService.getAll(token),
        sitesService.getAll(token)
      ]);
      
      // Hesap Yöneticisi veya Bireysel Hesap için sadece kendi sitesinin tarifelerini göster
      if ((isEffectiveHesapYoneticisi || isEffectiveBireyselHesap) && effectiveSiteId) {
        tariffsData = (tariffsData || []).filter(t => t.site_id === effectiveSiteId);
        sitesData = (sitesData || []).filter(s => s.id === effectiveSiteId);
      }
      
      setTariffs(tariffsData);
      setSites(sitesData);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  }, [token, isEffectiveHesapYoneticisi, effectiveSiteId]);
  
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  const resetForm = () => {
    setForm({
      name: '',
      site_id: isSiteManager ? user?.site_id : '',
      price_per_kwh: '',
      currency: 'TRY',
      start_time: '00:00',
      end_time: '23:59',
      day_type: 'everyday',
      is_active: true
    });
  };
  
  const openCreateModal = () => {
    setModalMode('create');
    setEditingTariffId(null);
    resetForm();
    setModalOpen(true);
  };
  
  const openEditModal = (tariff) => {
    setModalMode('edit');
    setEditingTariffId(tariff.id);
    setForm({
      name: tariff.name,
      site_id: tariff.site_id,
      price_per_kwh: tariff.price_per_kwh.toString(),
      currency: tariff.currency,
      start_time: tariff.start_time,
      end_time: tariff.end_time,
      day_type: tariff.day_type,
      is_active: tariff.is_active
    });
    setModalOpen(true);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.name || !form.site_id || !form.price_per_kwh) {
      toast.error('Tüm zorunlu alanları doldurun');
      return;
    }
    
    try {
      setSaving(true);
      
      const data = {
        ...form,
        price_per_kwh: parseFloat(form.price_per_kwh)
      };
      
      if (modalMode === 'create') {
        await tariffsService.create(token, data);
        toast.success('Tarife oluşturuldu');
      } else {
        await tariffsService.update(token, editingTariffId, data);
        toast.success('Tarife güncellendi');
      }
      
      setModalOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };
  
  const handleDelete = async () => {
    if (!deletingTariff) return;
    
    try {
      setSaving(true);
      await tariffsService.delete(token, deletingTariff.id);
      toast.success('Tarife silindi');
      setDeleteModalOpen(false);
      setDeletingTariff(null);
      fetchData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };
  
  const getSiteName = (siteId) => {
    const site = sites.find(s => s.id === siteId);
    return site?.name || 'Bilinmeyen Site';
  };
  
  const getCurrencySymbol = (currency) => {
    const symbols = { TRY: '₺', USD: '$', EUR: '€' };
    return symbols[currency] || currency;
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Tarifeler</h1>
          <p className="text-muted-foreground">Site bazlı fiyatlandırma yönetimi</p>
        </div>
        <div className="flex gap-2">
          <NativeButton onClick={fetchData} variant="secondary">
            <RefreshCw className="h-4 w-4 mr-2" />
            Yenile
          </NativeButton>
          {canCreateTariff && (
            <NativeButton onClick={openCreateModal} data-testid="create-tariff-btn">
              <Plus className="h-4 w-4 mr-2" />
              Yeni Tarife
            </NativeButton>
          )}
        </div>
      </div>
      
      {/* Tariffs Table */}
      <NativeCard>
        <NativeCardHeader>
          <NativeCardTitle>Tarife Listesi</NativeCardTitle>
        </NativeCardHeader>
        <NativeCardContent>
          <NativeTable>
            <NativeTableHeader>
              <NativeTableHead>Tarife Adı</NativeTableHead>
              <NativeTableHead>Site</NativeTableHead>
              <NativeTableHead>Fiyat</NativeTableHead>
              <NativeTableHead>Saat Aralığı</NativeTableHead>
              <NativeTableHead>Günler</NativeTableHead>
              <NativeTableHead>Durum</NativeTableHead>
              <NativeTableHead>İşlemler</NativeTableHead>
            </NativeTableHeader>
            <NativeTableBody>
              {tariffs.length === 0 ? (
                <NativeTableRow>
                  <NativeTableCell colSpan={7}>
                    <EmptyState
                      icon={DollarSign}
                      title="Tarife Yok"
                      description="Henüz tarife tanımlanmamış"
                      action={canCreateTariff && (
                        <NativeButton onClick={openCreateModal} size="sm">
                          <Plus className="h-4 w-4 mr-2" />
                          İlk Tarifeyi Oluştur
                        </NativeButton>
                      )}
                    />
                  </NativeTableCell>
                </NativeTableRow>
              ) : (
                tariffs.map(tariff => (
                  <NativeTableRow key={tariff.id}>
                    <NativeTableCell className="font-medium">
                      {tariff.name}
                    </NativeTableCell>
                    <NativeTableCell>
                      {getSiteName(tariff.site_id)}
                    </NativeTableCell>
                    <NativeTableCell>
                      <span className="font-semibold text-emerald-600">
                        {tariff.price_per_kwh.toFixed(2)} {getCurrencySymbol(tariff.currency)}/kWh
                      </span>
                    </NativeTableCell>
                    <NativeTableCell>
                      <div className="flex items-center gap-1">
                        <Clock className="h-4 w-4 text-muted-foreground" />
                        {tariff.start_time} - {tariff.end_time}
                      </div>
                    </NativeTableCell>
                    <NativeTableCell>
                      <NativeBadge variant={tariff.day_type === 'everyday' ? 'default' : 'secondary'}>
                        {DAY_TYPE_LABELS[tariff.day_type]}
                      </NativeBadge>
                    </NativeTableCell>
                    <NativeTableCell>
                      <NativeBadge variant={tariff.is_active ? 'success' : 'secondary'}>
                        {tariff.is_active ? 'Aktif' : 'Pasif'}
                      </NativeBadge>
                    </NativeTableCell>
                    <NativeTableCell>
                      <div className="flex gap-1">
                        <NativeButton
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditModal(tariff)}
                          data-testid={`edit-tariff-${tariff.id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </NativeButton>
                        {isSuperAdmin() && (
                          <NativeButton
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              setDeletingTariff(tariff);
                              setDeleteModalOpen(true);
                            }}
                            className="text-red-500 hover:text-red-600"
                            data-testid={`delete-tariff-${tariff.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </NativeButton>
                        )}
                      </div>
                    </NativeTableCell>
                  </NativeTableRow>
                ))
              )}
            </NativeTableBody>
          </NativeTable>
        </NativeCardContent>
      </NativeCard>
      
      {/* Create/Edit Modal */}
      <NativeModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={modalMode === 'create' ? 'Yeni Tarife Oluştur' : 'Tarife Düzenle'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <NativeInput
            label="Tarife Adı"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Örn: Gündüz Tarifesi"
            required
          />
          
          {isSiteManager ? (
            <div>
              <label className="block text-sm font-medium mb-1">Site</label>
              <div className="p-3 bg-muted rounded-lg">
                {getSiteName(user?.site_id)}
              </div>
              <p className="text-xs text-blue-500 mt-1">
                Site yöneticisi olarak sadece kendi siteniz için tarife oluşturabilirsiniz
              </p>
            </div>
          ) : (
            <NativeSelect
              label="Site"
              value={form.site_id}
              onChange={(e) => setForm({ ...form, site_id: e.target.value })}
              options={sites.map(s => ({ value: s.id, label: s.name }))}
              placeholder="Site Seçin"
              required
              data-testid="tariff-site-select"
            />
          )}
          
          <div className="grid grid-cols-2 gap-4">
            <NativeInput
              label="Fiyat (kWh)"
              type="number"
              step="0.01"
              min="0"
              value={form.price_per_kwh}
              onChange={(e) => setForm({ ...form, price_per_kwh: e.target.value })}
              placeholder="5.00"
              required
            />
            <NativeSelect
              label="Para Birimi"
              value={form.currency}
              onChange={(e) => setForm({ ...form, currency: e.target.value })}
              options={CURRENCY_OPTIONS}
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <NativeInput
              label="Başlangıç Saati"
              type="time"
              value={form.start_time}
              onChange={(e) => setForm({ ...form, start_time: e.target.value })}
            />
            <NativeInput
              label="Bitiş Saati"
              type="time"
              value={form.end_time}
              onChange={(e) => setForm({ ...form, end_time: e.target.value })}
            />
          </div>
          
          <NativeSelect
            label="Geçerli Günler"
            value={form.day_type}
            onChange={(e) => setForm({ ...form, day_type: e.target.value })}
            options={DAY_TYPE_OPTIONS}
          />
          
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is_active"
              checked={form.is_active}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
              className="rounded"
            />
            <label htmlFor="is_active" className="text-sm">Aktif</label>
          </div>
          
          <div className="flex justify-end gap-2 pt-4">
            <NativeButton type="button" variant="secondary" onClick={() => setModalOpen(false)}>
              İptal
            </NativeButton>
            <NativeButton type="submit" disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {modalMode === 'create' ? 'Oluştur' : 'Güncelle'}
            </NativeButton>
          </div>
        </form>
      </NativeModal>
      
      {/* Delete Confirmation Modal */}
      <NativeModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Tarife Silme Onayı"
      >
        <p className="text-muted-foreground mb-4">
          <strong>"{deletingTariff?.name}"</strong> tarifesini silmek istediğinize emin misiniz?
        </p>
        <div className="flex justify-end gap-2">
          <NativeButton variant="secondary" onClick={() => setDeleteModalOpen(false)}>
            İptal
          </NativeButton>
          <NativeButton 
            variant="destructive" 
            onClick={handleDelete}
            disabled={saving}
          >
            {saving ? 'Siliniyor...' : 'Sil'}
          </NativeButton>
        </div>
      </NativeModal>
    </div>
  );
}
